import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { notifyCohortsChanged } from "@/components/instructor/cohorts-changed";
import { apiErrorText } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";

type Cohort = {
  id: string;
  name: string;
  memberCount: number;
  isDefault: boolean;
};

type EnrollmentRow = {
  enrollmentId: string;
  userId: string;
  studentName: string | null;
  studentEmail: string;
  status: string;
  cohortId: string;
  cohortName: string;
};

type Props = {
  courseId: string;
};

export function CourseCohortsPanel({ courseId }: Props) {
  const t = useTranslations("instructor");
  // C4 (CRS-16): server errors are shown by code, never as the server's English.
  const tRoot = useTranslations();
  const errorText = (body: unknown, fallbackKey: string) =>
    apiErrorText(body, tRoot, `instructor.${fallbackKey}`, { namespace: "instructor.errors" });
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [enrollments, setEnrollments] = useState<EnrollmentRow[]>([]);
  const [name, setName] = useState("");
  const [posting, setPosting] = useState(false);
  const [movingUserId, setMovingUserId] = useState<string | null>(null);
  const [deletingCohortId, setDeletingCohortId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    void (async () => {
      setError(null);
      const [cohortRes, enrollRes] = await Promise.all([
        apiFetch(`/v1/instructor/courses/${courseId}/cohorts`),
        apiFetch(`/v1/instructor/courses/${courseId}/enrollments`),
      ]);
      const cohortJson = await cohortRes.json();
      const enrollJson = await enrollRes.json();
      if (cohortRes.ok) {
        setCohorts(cohortJson.data.cohorts as Cohort[]);
      } else {
        setCohorts([]);
        setError(errorText(cohortJson, "cohortsLoadFailed"));
      }
      if (enrollRes.ok) {
        setEnrollments(enrollJson.data.enrollments as EnrollmentRow[]);
      } else {
        setEnrollments([]);
        setError((prev) => prev ?? errorText(enrollJson, "cohortAssignLoadFailed"));
      }
    })();
  }, [courseId, reloadToken, t]);

  async function handleCreate(event: React.FormEvent) {
    event.preventDefault();
    setPosting(true);
    setError(null);
    try {
      const res = await apiFetch(`/v1/instructor/courses/${courseId}/cohorts`, {
        method: "POST",
        body: JSON.stringify({ name: name.trim() }),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(errorText(json, "cohortCreateFailed"));
        return;
      }
      setName("");
      setReloadToken((value) => value + 1);
      notifyCohortsChanged(courseId);
    } finally {
      setPosting(false);
    }
  }

  async function handleMove(userId: string, nextCohortId: string) {
    setMovingUserId(userId);
    setError(null);
    try {
      const res = await apiFetch(`/v1/instructor/courses/${courseId}/enrollments/cohort`, {
        method: "PATCH",
        body: JSON.stringify({ userId, cohortId: nextCohortId }),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(errorText(json, "cohortAssignFailed"));
        return;
      }
      setReloadToken((value) => value + 1);
    } finally {
      setMovingUserId(null);
    }
  }

  async function handleDelete(cohort: Cohort) {
    if (cohort.isDefault) return;
    if (!window.confirm(t("cohortDeleteConfirm", { name: cohort.name }))) return;
    setDeletingCohortId(cohort.id);
    setError(null);
    try {
      const res = await apiFetch(`/v1/instructor/courses/${courseId}/cohorts`, {
        method: "DELETE",
        body: JSON.stringify({ cohortId: cohort.id }),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(errorText(json, "cohortDeleteFailed"));
        return;
      }
      setReloadToken((value) => value + 1);
      notifyCohortsChanged(courseId);
    } finally {
      setDeletingCohortId(null);
    }
  }

  return (
    <section data-testid="course-cohorts-panel">
      <h2 className="text-lg font-medium">{t("cohortsTitle")}</h2>
      <p className="mt-1 text-sm text-[var(--text-muted)]">{t("cohortsHint")}</p>

      <ul className="mt-4 space-y-2" data-testid="course-cohorts-list">
        {cohorts.map((cohort) => (
          <li
            key={cohort.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded border border-[var(--border)] px-3 py-2 text-sm"
            data-testid={`cohort-row-${cohort.id}`}
            data-cohort-name={cohort.name}
          >
            <span>
              {cohort.name}
              {cohort.isDefault ? (
                <span className="ml-2 text-xs text-[var(--text-muted)]">
                  {t("cohortDefaultBadge")}
                </span>
              ) : null}
            </span>
            <span className="flex items-center gap-3">
              <span
                className="text-[var(--text-muted)]"
                data-testid={`cohort-members-${cohort.id}`}
              >
                {t("cohortMemberCount", { count: cohort.memberCount })}
              </span>
              {!cohort.isDefault ? (
                <button
                  type="button"
                  disabled={deletingCohortId === cohort.id}
                  onClick={() => void handleDelete(cohort)}
                  className="text-xs text-red-700 hover:underline disabled:opacity-50"
                  data-testid={`cohort-delete-${cohort.id}`}
                >
                  {deletingCohortId === cohort.id ? t("cohortDeleting") : t("cohortDelete")}
                </button>
              ) : null}
            </span>
          </li>
        ))}
      </ul>

      <form onSubmit={(e) => void handleCreate(e)} className="mt-4 flex flex-wrap gap-2">
        <input
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t("cohortNamePlaceholder")}
          className="min-w-[12rem] flex-1 rounded border px-3 py-2 text-sm border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
          data-testid="cohort-name-input"
          required
        />
        <button
          type="submit"
          disabled={posting || !name.trim()}
          className="rounded bg-[var(--ink-12)] px-4 py-2 text-sm text-[var(--ink-1)] disabled:opacity-60"
          data-testid="cohort-create-submit"
        >
          {posting ? t("cohortCreating") : t("cohortCreate")}
        </button>
      </form>

      <div className="mt-6" data-testid="course-cohort-assign">
        <h3 className="text-sm font-medium">{t("cohortAssignTitle")}</h3>
        <p className="mt-1 text-xs text-[var(--text-muted)]">{t("cohortAssignHint")}</p>
        {enrollments.length === 0 ? (
          <p className="mt-3 text-sm text-[var(--text-muted)]">{t("cohortAssignEmpty")}</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {enrollments.map((row) => (
              <li
                key={row.enrollmentId}
                className="flex flex-wrap items-center justify-between gap-2 rounded border border-[var(--border)] px-3 py-2 text-sm"
                data-testid={`cohort-assign-row-${row.userId}`}
              >
                <span>
                  {row.studentName ?? row.studentEmail}
                  <span className="ml-2 text-xs text-[var(--text-muted)]">{row.status}</span>
                </span>
                <select
                  className="rounded border border-[var(--border-strong)] px-2 py-1 text-sm border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
                  value={row.cohortId}
                  disabled={movingUserId === row.userId || cohorts.length === 0}
                  onChange={(event) => void handleMove(row.userId, event.target.value)}
                  data-testid={`cohort-assign-select-${row.userId}`}
                >
                  {cohorts.map((cohort) => (
                    <option key={cohort.id} value={cohort.id}>
                      {cohort.name}
                    </option>
                  ))}
                </select>
              </li>
            ))}
          </ul>
        )}
      </div>

      {error ? (
        <p className="mt-2 text-sm text-red-600" data-testid="cohort-panel-error">
          {error}
        </p>
      ) : null}
    </section>
  );
}
